import { BezierShape } from '@brancisco/p5-bezier-builder'

class Mountains {
    constructor (data, colors) {
        this.beziers = data.map(data => new BezierShape(data))
        this.colors = colors ? colors : this.beziers.map(() => [255, 255, 255])
        this.width, this.height, this.rect
        this.calc()

        this.perspectiveShift = 0

        // events registered with the window
        // must remove these from window before destroying class instance
        this.handleResizeEvent
        this.handleScrollEvent
    }

    setColors (colors) {
        this.colors = colors 
    }

    calcBoundary () {
        const trbl = [[], [], [], []]
        this.beziers.forEach((b) => {
            for (let i in trbl) {
                trbl[i].push(b.rect[i])
            }
        })
        this.rect = [Math.min(...trbl[0]), Math.max(...trbl[1]), Math.max(...trbl[2]), Math.min(...trbl[3])]
        return this.rect
    }

    calcWidthHeight () {
        const [t, r, b, l] = this.calcBoundary()
        this.width = Math.abs(r - l)
        this.height = Math.abs(b - t)
        return [this.width, this.height]
    }

    calc () {
        this.calcWidthHeight()
    }

    setPosition (x, y) {
        const [t, r, b, l] = this.rect
        const shiftX = x - l
        const shiftY = y - b
        this.beziers.forEach(b => {
            b.shift(shiftX, shiftY)
        })
        this.calc()
    }

    changePerspective (p) {
        const diff = p - this.perspectiveShift
        const n = this.beziers.length
        this.perspectiveShift = p
        this.beziers.forEach((b, i) => {
            b.shift(0, (n-i+1)*diff*0.15)
        })
        this.calc()
    }

    setDim (w, h) {
        const [wd, hd] = [w - this.width, h - this.height]
        this.beziers.forEach((b, i) => {
            const [bw, bh] = b.getDim()
            b.setDim(bw + wd, bh + hd)
        })
        this.calc()
    }

    draw (p5) {
        p5.noStroke()
        for (let i in this.beziers) {
            p5.fill(this.colors[i])
            this.beziers[i].draw(p5)
        }
    }
}

class Car {
    constructor (x=0, y=0, vx=1, vy=0, w=30, h=15) {
        this.pos = [x, y]
        this.vel = [vx, vy]
        this.w = w
        this.h = h
    }

    setPos (x, y) {
        this.pos = [x, y ? y : this.pos[1]]
    }

    update () {
        if (this.vel[0]) this.pos[0] += this.vel[0]
        if (this.vel[1]) this.pos[1] += this.vel[1]
    }

    draw (p5) {
        p5.rect(this.pos[0], this.pos[1], this.w, this.h, 10)
    }
}

class MountainSketch {

    constructor (mountainData, fullwindow = false) {
        this.fullwindow = fullwindow
        this.background = [137, 196, 244]//[255, 255, 255]
        this.size = { w: 500, h: 500 }

        this.mountains = new Mountains(mountainData)
        this.cars = []
        this.baseMountainColor = [52, 73, 94]
    }

    getCenter () {
        return [this.size.w/2, this.size.h/2]
    }

    setMountainDim () {
        const max = Math.max(this.size.w + 100, 1200)
        this.mountains.setDim(max , this.mountains.height)
    }

    setMountainColors (p5) {
        const sy = (window.scrollY + 1) * 0.001
        this.mountains.setColors(this.mountains.beziers.reduce((acc, cur, i) => {
            const ip = this.mountains.beziers.length - i
            return [...acc, p5.lerpColor(p5.color(this.baseMountainColor), p5.color(255), ip*0.1+sy)]
        }, []))
    }

    initCars (n) {
        if (this.cars.length === 0) {
            for (let i = 0; i < n; i ++) {
                this.cars.push(new Car(
                    100*Math.random(), 
                    this.mountains.beziers[3].rect[2] - 10,
                    (0.3 + Math.random()) * this.randSign(), 
                    0))
            }
        }
    }

    updateWindowSize (p5) {
        const [w, h] = [this.size.w, this.size.h]
        this.size.w = Math.max(document.documentElement.clientWidth || 0, window.innerWidth || 0)
        this.size.h = Math.max(document.documentElement.clientHeight || 0, window.innerHeight || 0)

        this.setMountainDim()
        // this.size.h - this.mountains.height/2
        // this.mountains.rect[2]
        this.mountains.setPosition(this.size.w/2 - this.mountains.width/2, this.mountains.rect[2])
    }

    setup (p5) {
        
        this.mountains.setDim(this.mountains.width, this.mountains.height)

        this.handleResizeEvent = () => {
            this.updateWindowSize(p5)
            p5.resizeCanvas(this.size.w, this.size.h)
        }

        // set the mountain colors
        this.handleScrollEvent = () => {
            this.setMountainColors(p5)
            this.mountains.changePerspective(window.scrollY)
            // this.cars.forEach(car => {
            //     car.setPos(car.pos[0], this.mountains.beziers[3].rect[2] - 10)
            // })
        }

        return  () => {
            p5.noStroke()
            // if fullwindow is true we need to resize the canvas to fit the window
            if (this.fullwindow) {
                this.updateWindowSize(p5)
                window.addEventListener('resize', this.handleResizeEvent)
            }
            this.mountains.setPosition(
                this.size.w/2 - this.mountains.width/2,
                this.size.h - (this.size.h > 750 ? this.mountains.height/2 : this.mountains.height/5)
            )
            // this.initCars(5)

            window.addEventListener('scroll', this.handleScrollEvent)
            this.setMountainColors(p5)
            p5.createCanvas(this.size.w, this.size.h)
        }
    }

    draw (p5) {
        return () => {
            if (window.scrollY < this.size.h - 50) {
                p5.background(this.background)
                p5.fill([247, 202, 24])
                p5.ellipse(this.size.w/2 - 150, this.mountains.rect[0], 300, 300)
                this.mountains.draw(p5)
                p5.rect(0, this.mountains.beziers[3].rect[2] - 1, this.size.w, 1000)
                p5.fill(255)
                this.cars.forEach(car => {
                    car.draw(p5)
                    car.update()
                    if (car.pos[0] > this.size.w) car.setPos(0)
                    if (car.pos[0] < 0) car.setPos(this.size.w)
                })
            }
        }
    }

    randSign () {
        return Math.random() > .5 ? -1 : 1
    }

    sketch (p5) {
        p5.setup = this.setup(p5)
        p5.draw = this.draw(p5)
    }

    destroy () {
        if (this.handleResizeEvent) window.removeEventListener('resize', this.handleResizeEvent)
        if (this.handleScrollEvent) window.removeEventListener('scroll', this.handleScrollEvent)
    }

}

export default MountainSketch